/**
 * Pull a WebRTC stream over WHEP: POST our offer as SDP, take back an answer.
 *
 * Written against the browser's own RTCPeerConnection rather than a library,
 * because WHEP is one HTTP round trip and a dependency here would be most of a
 * megabyte to avoid forty lines.
 */

export interface WhepSession {
  close: () => void;
  /** inbound-rtp framesDecoded, refreshed twice a second. Null until the first report. */
  framesDecoded: () => number | null;
}

/** Non-trickle ICE: gather briefly, then send one offer with every candidate in it. */
function iceGathered(pc: RTCPeerConnection, capMs = 1500): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === 'complete') done();
    };
    pc.addEventListener('icegatheringstatechange', check);
    setTimeout(done, capMs);
  });
}

/**
 * Connect `video` to a WHEP endpoint. With a ticket, it goes in the
 * Authorization header: the gateway puts it to the API, which lets it open
 * this one path, once.
 *
 * One offer, no trickle. Trickled candidates would be PATCHed to a session URL
 * afterwards, and each PATCH is another request the gateway would have to
 * authorise with a ticket that has already been spent.
 */
export async function connectWhep(
  url: string,
  video: HTMLVideoElement,
  { ticket }: { ticket?: string } = {},
): Promise<WhepSession> {
  const pc = new RTCPeerConnection();
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });
  pc.ontrack = (event) => {
    if (event.streams[0]) video.srcObject = event.streams[0];
  };

  let decoded: number | null = null;
  const stats = setInterval(() => {
    void pc.getStats().then((report) => {
      report.forEach((entry: { type?: string; kind?: string; framesDecoded?: number }) => {
        if (entry.type === 'inbound-rtp' && entry.kind === 'video' && entry.framesDecoded !== undefined) {
          decoded = entry.framesDecoded;
        }
      });
    });
  }, 500);
  const close = () => {
    clearInterval(stats);
    pc.close();
  };

  try {
    await pc.setLocalDescription(await pc.createOffer());
    await iceGathered(pc);
    const headers: Record<string, string> = { 'Content-Type': 'application/sdp' };
    if (ticket) headers.Authorization = `Bearer ${ticket}`;
    const response = await fetch(url.replace(/^webrtc:\/\//, 'https://'), {
      method: 'POST',
      headers,
      body: pc.localDescription?.sdp ?? '',
    });
    if (!response.ok) {
      throw new Error(
        response.status === 400
          ? 'The gateway could not offer this camera in a format this browser decodes.'
          : `The gateway refused the connection (${response.status}).`,
      );
    }
    await pc.setRemoteDescription({ type: 'answer', sdp: await response.text() });
  } catch (error) {
    close();
    throw error;
  }
  return { close, framesDecoded: () => decoded };
}
